// Centralised, defensive mapping from the EI credits API's untyped response dicts to the
// shapes the AI Usage screens render. The live API returns loosely-typed JSON whose exact
// keys are still being confirmed against staging, so every field is read through `pick`
// with a list of candidate keys and a sensible fallback. When staging reveals the real
// key names, fix them HERE — the components never read raw response fields directly.

// Arrays are rejected outright: JS coerces [] to 0 and [5] to 5, so a key that holds a LIST
// (the API's `messages` array of turns, for example) would otherwise masquerade as a count.
const num = (value) => {
  if (Array.isArray(value)) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

// First present (non-null) value among candidate keys, else the fallback.
export const pick = (obj, keys, fallback = undefined) => {
  if (!obj) return fallback;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return fallback;
};

// Responses are sometimes wrapped ({ data: ... }, { balance: ... }); unwrap one level.
const body = (raw, wrapperKeys = []) => {
  if (!raw) return {};
  for (const key of ['data', ...wrapperKeys]) {
    if (raw[key] && typeof raw[key] === 'object' && !Array.isArray(raw[key])) return raw[key];
  }
  return raw;
};

// Coerce whatever list-shape came back (bare array, or nested under a common key) to array.
const asList = (raw, keys = []) => {
  if (Array.isArray(raw)) return raw;
  for (const key of ['data', 'items', 'results', ...keys]) {
    if (Array.isArray(raw?.[key])) return raw[key];
  }
  return [];
};

// The API sends `pct` as an already-scaled percentage figure (0.73 means 0.73%, 2.2 means 2.2%,
// 83 means 83%) — NOT a 0..1 fraction. So use it verbatim; only when it's absent do we derive it
// from used/total. (The old ≤1→×100 heuristic wrongly turned 0.83% into 83%.)
const percentUsed = (obj, used, total) => {
  const raw = pick(obj, ['pctUsed', 'pct_used', 'pct', 'percent_used', 'usage_pct', 'usagePercent']);
  if (raw !== undefined && raw !== null) return num(raw);
  return total > 0 ? (used / total) * 100 : 0;
};

// Staff on internal Welbee / EDU-Intelligence accounts are excluded from customer-facing
// seat and usage counts where the email is present to identify them.
export const isInternalUser = (email) =>
  typeof email === 'string' && /@(welbee|edu-intelligence)\b/i.test(email);

export function mapUser(raw) {
  const total = num(pick(raw, ['allowance', 'total', 'total_allowance', 'limit', 'monthly_allowance']));
  const used = num(pick(raw, ['used', 'consumed', 'credits_used', 'used_credits']));
  const remainingRaw = pick(raw, ['remaining', 'credits_remaining']);
  // The credits API returns first_name/last_name (often null — real names live in Identity,
  // not EI). Compose them, then fall back to any single name field, then email.
  const composed = [pick(raw, ['first_name', 'firstName']), pick(raw, ['last_name', 'lastName'])]
    .filter(Boolean)
    .join(' ')
    .trim();
  const email = pick(raw, ['email', 'user_email']);
  return {
    userId: pick(raw, ['user_id', 'userId', 'id']),
    name: pick(raw, ['name', 'user_name', 'display_name', 'full_name']) || composed || email || '',
    email,
    schoolId: pick(raw, ['school_id', 'schoolId']),
    schoolName: pick(raw, ['school', 'school_name', 'schoolName']),
    used,
    allowance: total,
    remaining: remainingRaw !== undefined ? num(remainingRaw) : Math.max(0, total - used),
    usagePct: percentUsed(raw, used, total),
    threads: num(pick(raw, ['threads', 'thread_count', 'no_of_threads'])),
    messages: num(pick(raw, ['messages', 'message_count', 'no_of_messages'])),
    raw,
  };
}

const mapUsers = (list) =>
  asList(list)
    .map(mapUser)
    .filter((u) => !isInternalUser(u.email));

// The pooled "System / async tasks" allocation, shown as its own row beneath the users.
function mapAsyncPot(obj) {
  const pot = pick(obj, ['async_pot', 'async', 'system', 'async_tasks', 'system_pot']);
  if (pot === undefined || pot === null) return null;
  if (typeof pot === 'number') return { used: num(pot), allowance: 0, usagePct: 0 };
  const total = num(pick(pot, ['allowance', 'total', 'limit']));
  const used = num(pick(pot, ['used', 'consumed', 'credits_used']));
  return { used, allowance: total, usagePct: percentUsed(pot, used, total) };
}

export function mapSchool(raw) {
  const total = num(pick(raw, ['allowance', 'total', 'total_allowance', 'limit']));
  const used = num(pick(raw, ['used', 'consumed', 'credits_used']));
  return {
    schoolId: pick(raw, ['school_id', 'schoolId', 'id']),
    name: pick(raw, ['name', 'school', 'school_name'], ''),
    used,
    allowance: total,
    usagePct: percentUsed(raw, used, total),
    raw,
  };
}

// One school node of a group balance: its own users + async pot rolled into a school row for the
// account view's Schools list, with the members flattened out (tagged with the school name).
function nestedSchoolBalance(s) {
  const members = mapUsers(pick(s, ['users'], []));
  const asyncPot = mapAsyncPot(s);
  const name = pick(s, ['school_name', 'name', 'school'], '');
  const used = members.reduce((a, u) => a + u.used, 0) + (asyncPot ? asyncPot.used : 0);
  const allowance = members.reduce((a, u) => a + u.allowance, 0) + (asyncPot ? asyncPot.allowance : 0);
  return {
    schoolId: pick(s, ['school_id', 'schoolId', 'id']),
    name,
    used,
    allowance,
    usagePct: allowance > 0 ? (used / allowance) * 100 : 0,
    seats: num(pick(s, ['seats', 'seat_count'])),
    asyncPot,
    members: members.map((u) => ({ ...u, schoolName: u.schoolName || name })),
    raw: s,
  };
}

// School- or group-level balance for the account view summary tiles + usage bar.
// The schools/{id}/balance endpoint returns NO top-level totals — allowance/consumed live
// per-user in `users[]` plus a pooled `async_pot`. So when the top-level fields are absent
// we aggregate the headline totals from the users (+ the async pot); when they are present
// (other shapes) we use them directly.
export function mapBalance(raw) {
  const b = body(raw, ['balance', 'summary']);

  // GROUP balance is a nested tree: schools[] each with their own users[] + async_pot, plus a
  // top-level group_pot and group_total — there is NO flat top-level users[]. Detect it and
  // aggregate the group's headline totals, flatten every school's members, and roll each school
  // into a row for the Schools list (otherwise the tab shows "No data" — the flat path below
  // finds an empty users[] and a 0 total).
  const schoolNodes = asList(pick(b, ['schools', 'school_balances'], []));
  const groupPot = pick(b, ['group_pot']);
  const isNestedGroup =
    pick(b, ['group_total']) !== undefined ||
    groupPot != null ||
    schoolNodes.some((s) => Array.isArray(s?.users) || s?.async_pot);

  if (isNestedGroup && (schoolNodes.length || groupPot)) {
    const schools = schoolNodes.map(nestedSchoolBalance);
    const groupMembers = groupPot ? mapUsers(pick(groupPot, ['users'], [])).map((u) => ({ ...u, schoolName: 'Group level' })) : [];
    const groupAsync = groupPot ? mapAsyncPot(groupPot) : null;

    const used =
      schools.reduce((a, s) => a + s.used, 0) +
      groupMembers.reduce((a, u) => a + u.used, 0) +
      (groupAsync ? groupAsync.used : 0);
    const total =
      num(pick(b, ['group_total', 'allowance', 'total', 'total_allowance', 'limit'])) ||
      schools.reduce((a, s) => a + s.allowance, 0) +
        groupMembers.reduce((a, u) => a + u.allowance, 0) +
        (groupAsync ? groupAsync.allowance : 0);

    // A single "System / async tasks" summary across every school's pool + the group pot.
    const asyncUsed = schools.reduce((a, s) => a + (s.asyncPot ? s.asyncPot.used : 0), 0) + (groupAsync ? groupAsync.used : 0);
    const asyncAllowance =
      schools.reduce((a, s) => a + (s.asyncPot ? s.asyncPot.allowance : 0), 0) + (groupAsync ? groupAsync.allowance : 0);

    return {
      total,
      used,
      remaining: Math.max(0, total - used),
      usagePct: total > 0 ? (used / total) * 100 : 0,
      seats: schools.reduce((a, s) => a + (s.seats || 0), 0) + (groupPot ? num(pick(groupPot, ['seats', 'seat_count'])) : 0),
      renewalDate: pick(b, ['renews_at', 'renewal_date', 'renews_on', 'period_end', 'resets_at']),
      asyncPot: asyncUsed || asyncAllowance ? { used: asyncUsed, allowance: asyncAllowance, usagePct: asyncAllowance > 0 ? (asyncUsed / asyncAllowance) * 100 : 0 } : null,
      users: [...schools.flatMap((s) => s.members), ...groupMembers],
      schools: schools.map(({ schoolId, name, used: u, allowance, usagePct }) => ({ schoolId, name, used: u, allowance, usagePct })),
      raw: b,
    };
  }

  const users = mapUsers(pick(b, ['users', 'user_balances', 'members'], []));
  const asyncPot = mapAsyncPot(b);

  const topTotal = pick(b, ['allowance', 'total', 'total_allowance', 'limit', 'total_credits']);
  const topUsed = pick(b, ['used', 'consumed', 'credits_used', 'used_credits']);
  const topRemaining = pick(b, ['remaining', 'balance', 'credits_remaining', 'remaining_credits']);

  const sumAllowance =
    users.reduce((a, u) => a + u.allowance, 0) + (asyncPot ? asyncPot.allowance : 0);
  const sumUsed = users.reduce((a, u) => a + u.used, 0) + (asyncPot ? asyncPot.used : 0);

  const total = topTotal !== undefined ? num(topTotal) : sumAllowance;
  const used = topUsed !== undefined ? num(topUsed) : sumUsed;
  const remaining = topRemaining !== undefined ? num(topRemaining) : Math.max(0, total - used);

  return {
    total,
    used,
    remaining,
    usagePct: total > 0 ? (used / total) * 100 : percentUsed(b, used, total),
    seats: pick(b, ['seats', 'seat_count']),
    renewalDate: pick(b, ['renews_at', 'renewal_date', 'renews_on', 'period_end', 'resets_at']),
    asyncPot,
    users,
    schools: asList(pick(b, ['schools', 'school_balances'], [])).map(mapSchool),
    raw: b,
  };
}

// --- Platform (dashboard home) ---------------------------------------------

export function mapPlatformSummary(raw) {
  const s = body(raw, ['summary']);
  return {
    // The summary endpoint reports "credits consumed"; include the `consumed` spellings (the
    // headline showed 0 because only the `*_used` variants were read).
    totalCreditsUsed: num(
      pick(s, [
        'total_credits_used', 'credits_used', 'total_credits', 'credits',
        'total_consumed', 'credits_consumed', 'total_credits_consumed', 'consumed',
      ])
    ),
    // The platform summary spells its currency figure `approx_total_cost_usd` (the per-scope
    // endpoints use `approx_cost_usd`), so both are read. When absent the UI derives cost
    // from credits.
    totalCost: pick(s, [
      'approx_total_cost_usd', 'total_cost', 'cost', 'total_cost_usd', 'approx_cost_usd', 'cost_usd',
    ]),
    totalInteractions: num(pick(s, ['total_interactions', 'interactions', 'total_messages', 'messages'])),
    activeOrgs: num(pick(s, ['active_orgs', 'org_count', 'active_organisations', 'orgs'])),
    raw: s,
  };
}

// --- Platform Usage list, from /api/admin/credits/orgs/summary --------------
// The lightweight per-org rollup (id, type, resolved name, group_total, consumed, pct) that
// replaced the heavy /balances fan-out. Each row is a group OR a standalone school ("individual"
// dataset). Per-org user/message counts aren't in this endpoint — they live in the drill-in
// trust overview — so this list intentionally shows Credits + % only.
export function mapOrgSummaryRows(raw) {
  return asList(raw, ['orgs', 'items', 'rows', 'results']).map((r) => {
    const type = normaliseType(pick(r, ['type', 'org_type', 'scope']));
    const consumed = num(pick(r, ['consumed', 'credits_consumed', 'credits', 'used', 'credits_used']));
    const allowance = num(pick(r, ['group_total', 'allocation', 'allowance', 'total', 'limit']));
    // The row carries the org id; keep any resolved group/school id for the drill-in route.
    const orgId = pick(r, ['org_id', 'id']);
    const groupId = pick(r, ['group_id', 'groupId']);
    const schoolId = pick(r, ['school_id', 'schoolId']);
    return {
      id: pick(r, ['id', 'org_id']),
      orgId,
      name: pick(r, ['name', 'org_name', 'organisation', 'resolved_name'], undefined) || pick(r, ['external_ref', 'tenant_dataset']) || '—',
      type,
      groupId,
      schoolId,
      credits: consumed,
      // `pct` is already a percentage figure (see percentUsed) — use it as-is, else derive it.
      allowancePct: pick(r, ['pct', 'usage_pct', 'pct_used']) != null
        ? num(pick(r, ['pct', 'usage_pct', 'pct_used']))
        : (allowance > 0 ? (consumed / allowance) * 100 : 0),
      raw: r,
    };
  });
}

const normaliseType = (value) => {
  const t = String(value || '').toLowerCase();
  if (t.includes('group') || t.includes('mat')) return 'GROUP';
  // The balances endpoint labels standalone schools "individual"; render them as SCHOOL.
  if (t.includes('school') || t.includes('individual')) return 'SCHOOL';
  return undefined;
};

function indexOrgs(orgs) {
  const map = new Map();
  asList(orgs, ['orgs']).forEach((o) => {
    const id = pick(o, ['org_id', 'id', 'school_id', 'group_id']);
    if (id === undefined || id === null) return;
    map.set(String(id), {
      name: pick(o, ['name', 'org_name', 'organisation', 'school_name', 'group_name']),
      type: normaliseType(pick(o, ['type', 'scope', 'org_type'])),
      schoolId: pick(o, ['school_id', 'schoolId']),
      groupId: pick(o, ['group_id', 'groupId']),
    });
  });
  return map;
}

// The platform balances endpoint returns each org as a NESTED tree — no org-level
// `credits`/`users` fields. Consumption lives per-user inside every school (`schools[].users[]`)
// plus each school's pooled `async_pot`, plus the group-level `group_pot` (its own users +
// async). The org's allowance is `group_total`. Detect that shape so we aggregate it rather
// than reading flat fields that are always absent (which showed 0 across the whole grid).
const isNestedOrg = (r) =>
  Array.isArray(r?.schools) || (r?.group_pot && typeof r.group_pot === 'object');

function aggregateOrg(r) {
  const seen = new Set();
  let used = 0;
  let userCount = 0;

  // A user_id can appear both in a school and in the group pot; sum every `consumed` (each is a
  // distinct pull from a different pool) but count each distinct user once.
  const absorbUsers = (list) => {
    asList(list).forEach((u) => {
      if (isInternalUser(pick(u, ['email', 'user_email']))) return;
      used += num(pick(u, ['consumed', 'used', 'credits_used']));
      const uid = pick(u, ['user_id', 'userId', 'id']);
      const key = uid != null ? String(uid) : `anon-${seen.size}`;
      if (!seen.has(key)) {
        seen.add(key);
        userCount += 1;
      }
    });
  };
  const absorbAsync = (pot) => {
    if (pot && typeof pot === 'object') used += num(pick(pot, ['consumed', 'used', 'credits_used']));
  };

  asList(r.schools).forEach((s) => {
    absorbUsers(pick(s, ['users'], []));
    absorbAsync(pick(s, ['async_pot']));
  });
  const groupPot = pick(r, ['group_pot']);
  if (groupPot) {
    absorbUsers(pick(groupPot, ['users'], []));
    absorbAsync(pick(groupPot, ['async_pot']));
  }

  return { used, userCount, allowance: num(pick(r, ['group_total', 'allowance', 'total'])) };
}

// One row per organisation for the Platform Usage table, enriched from getOrgs() metadata.
export function mapPlatformRows(balancesRaw, orgs) {
  const orgIndex = indexOrgs(orgs);
  return asList(balancesRaw, ['balances', 'orgs', 'rows']).map((r) => {
    const orgId = pick(r, ['org_id', 'id']);
    const type = normaliseType(pick(r, ['type', 'scope', 'org_type']));
    const schools = asList(r.schools);
    // Groups drill by their MAT group_id; standalone schools by school_id.
    const groupId = pick(r, ['group_id', 'groupId']) ?? pick(r?.group_pot, ['group_id']);
    const schoolId =
      pick(r, ['school_id', 'schoolId']) ?? (schools.length === 1 ? pick(schools[0], ['school_id']) : undefined);
    const lookup = groupId ?? schoolId ?? orgId;
    const meta = orgIndex.get(String(lookup)) || orgIndex.get(String(orgId)) || {};

    // A standalone school's name IS its single school's name; a group's name isn't in the
    // balances payload, so fall back to metadata then the tenant's external ref.
    const soloSchoolName = type !== 'GROUP' && schools.length === 1 ? pick(schools[0], ['school_name', 'name']) : undefined;
    const name =
      pick(r, ['name', 'org_name', 'organisation', 'group_name'], undefined) ||
      soloSchoolName ||
      meta.name ||
      pick(r, ['external_ref', 'tenant_dataset']) ||
      '—';

    let credits;
    let allowance;
    let users;
    if (isNestedOrg(r)) {
      const agg = aggregateOrg(r);
      credits = agg.used;
      allowance = agg.allowance;
      users = agg.userCount;
    } else {
      credits = num(pick(r, ['credits', 'credits_used', 'used']));
      allowance = num(pick(r, ['allowance', 'total', 'limit']));
      users = num(pick(r, ['users', 'user_count', 'active_users']));
    }

    return {
      id: lookup,
      name,
      type: type || meta.type,
      schoolId: schoolId ?? meta.schoolId,
      groupId: groupId ?? meta.groupId,
      users,
      // Per-org message counts aren't carried by the balances endpoint (only the summary +
      // leaderboard expose interactions); left at 0 until a per-org source exists.
      messages: num(pick(r, ['messages', 'message_count', 'interactions'])),
      credits,
      allowancePct: percentUsed(r, credits, allowance),
      raw: r,
    };
  });
}

// Sort keys the leaderboard "Sort by" dropdown offers. Each drives a server-side re-query via
// the `sort_by` param — the section does not reorder rows in memory.
export const LEADERBOARD_SORTS = [
  { key: 'messages', label: 'Messages Sent' },
  { key: 'threads', label: 'Threads' },
  { key: 'cost', label: 'Cost' },
  { key: 'credits', label: 'Credits' },
];

// A school-scoped leaderboard entry with a NEGATIVE id is a group-level credit pot emitted as a
// pseudo-school, keyed by the negated group id and carrying no name — several of them rank in the
// platform top ten, where they rendered as a bare em dash. `orgRows` (the per-customer rollup the
// page has already loaded) resolves them back to a real trust name.
const resolveLeaderboardName = (rawName, id, orgRows) => {
  if (rawName) return rawName;
  const numericId = Number(id);
  if (Number.isFinite(numericId) && numericId < 0) {
    const groupId = Math.abs(numericId);
    const org = asList(orgRows).find((o) => String(o?.groupId) === String(groupId));
    return org?.name ? `${org.name} — Group level` : `Group ${groupId} — Group level`;
  }
  // A school the rollup can't name is still better identified by its id than by a dash.
  return id ? `School ${id}` : '—';
};

export function mapLeaderboard(raw, orgRows = []) {
  // The reporting/leaderboard endpoint wraps its rows under `entries`; keep the other
  // candidate keys for resilience across shapes.
  return asList(raw, ['entries', 'leaderboard', 'schools', 'rows']).map((r) => {
    const credits = num(pick(r, ['credits', 'credits_used', 'used']));
    const allowance = num(pick(r, ['allowance', 'total', 'limit']));
    const pctRaw = pick(r, ['pctUsed', 'pct_used', 'pct', 'percent_used', 'usage_pct', 'usagePercent']);
    const id = pick(r, ['school_id', 'id', 'org_id', 'group_id', 'user_id']);
    return {
      id,
      name: resolveLeaderboardName(pick(r, ['name', 'school_name', 'org_name']), id, orgRows),
      messages: num(pick(r, ['messages', 'messages_sent', 'message_count', 'interactions'])),
      threads: num(pick(r, ['threads', 'thread_count', 'no_of_threads'])),
      credits,
      costUsd: num(pick(r, ['approx_cost_usd', 'cost', 'cost_usd'])),
      users: num(pick(r, ['users', 'user_count', 'active_users'])),
      // This endpoint reports no allowance and no pct, so consumption against allowance is
      // UNKNOWN here — null, not zero. Rendering a derived 0% claimed every school on the
      // platform had used none of its credits.
      usagePct: pctRaw != null || allowance > 0 ? percentUsed(r, credits, allowance) : null,
      raw: r,
    };
  });
}

// --- Conversation analytics (user detail) ----------------------------------

// The reporting overview reports `messages_sent` / `threads_started` and identifies the subject
// only by `scope_name`. It carries NO active-day count and no school/role, so `activeDays` is
// derived from the daily series by the caller (see `countActiveDays`) rather than invented here.
export function mapOverview(raw) {
  const o = body(raw, ['overview']);
  return {
    userName: pick(o, ['user_name', 'name', 'display_name', 'full_name', 'scope_name']),
    schoolName: pick(o, ['school', 'school_name']),
    role: pick(o, ['role', 'job_title', 'title']),
    totalMessages: num(pick(o, ['messages_sent', 'total_messages', 'message_count', 'messages'])),
    threadsStarted: num(pick(o, ['threads_started', 'threads', 'thread_count'])),
    creditsUsed: num(pick(o, ['credits_used', 'credits', 'total_credits'])),
    activeDays: num(pick(o, ['active_days', 'days_active', 'active_day_count'])),
    raw: o,
  };
}

// Days on which anything actually happened. The overview has no such figure and the daily
// endpoint omits zero-activity days entirely, so count the days carrying real usage — taken
// BEFORE `fillDailyGaps` pads the month out with zeroes.
export function countActiveDays(days) {
  return asList(days).filter((d) => num(d?.credits) > 0 || num(d?.messages) > 0).length;
}

// A thread row carries BOTH a `message_count` and (with include=breakdown|messages) a `messages`
// ARRAY of the turns themselves. `message_count` must therefore be read first: reading `messages`
// first yielded Number([...]) === NaN, so every row rendered "0 messages" against real data.
export function mapThreads(raw) {
  return asList(raw, ['threads', 'conversations', 'rows']).map((t) => {
    const credits = num(pick(t, ['credits', 'credits_used', 'used']));
    const allowance = num(pick(t, ['allowance', 'total']));
    const feedback = pick(t, ['feedback']) || {};
    return {
      id: pick(t, ['thread_id', 'id', 'conversation_id']),
      date: pick(t, ['date', 'created_at', 'started_at', 'timestamp']),
      lastMessageAt: pick(t, ['last_message_at', 'lastMessageAt']),
      title: pick(t, ['title', 'thread_title', 'subject']),
      ownerName: pick(t, ['owner_name', 'owner', 'user_name', 'name']),
      messages: num(pick(t, ['message_count', 'no_of_messages', 'messages'])),
      credits,
      usagePct: percentUsed(t, credits, allowance),
      // Thumbs up/down already ride along on the thread payload, so the reaction counts need no
      // second request to the dedicated feedback endpoint.
      positiveFeedback: num(pick(feedback, ['positive'])),
      negativeFeedback: num(pick(feedback, ['negative'])),
      raw: t,
    };
  });
}

// The threads endpoint scopes its FIGURES to `period` but not its LISTING — it returns every
// thread the subject has ever created, for any period asked (including months before the first
// thread existed). Every other figure on the screen is period-scoped, so a conversation is kept
// when it saw activity in the period or was started during it; otherwise the list contradicts the
// month the operator selected.
export function filterThreadsForPeriod(threads, period) {
  if (!period) return threads;
  return asList(threads).filter(
    (t) =>
      num(t?.messages) > 0 ||
      num(t?.credits) > 0 ||
      String(t?.date || '').startsWith(period)
  );
}

export function mapThreadMessages(raw) {
  const t = body(raw, ['thread', 'conversation']);
  return asList(t, ['messages', 'turns', 'entries']).map((m) => ({
    role: pick(m, ['role', 'author', 'sender'], 'assistant'),
    content: pick(m, ['content', 'text', 'message', 'body'], ''),
    timestamp: pick(m, ['timestamp', 'created_at', 'time']),
    credits: num(pick(m, ['credits', 'credits_used'])),
  }));
}

// Normalises the daily-usage endpoint's `days` array (one entry per active day) into ascending
// `{ date, credits, messages, costUsd }` rows. Zero-activity days are absent here — see
// `fillDailyGaps` to expand a period into a continuous run of days for charting.
export function mapDailyUsage(raw) {
  return asList(raw, ['days', 'daily', 'series'])
    .map((d) => ({
      date: pick(d, ['date', 'day']),
      credits: num(pick(d, ['credits', 'credits_used', 'used'])),
      messages: num(pick(d, ['messages', 'message_count'])),
      costUsd: num(pick(d, ['approx_cost_usd', 'cost', 'cost_usd'])),
    }))
    .filter((d) => d.date)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

// Expand a period (YYYY-MM) into every calendar day, carrying real values through and filling
// the omitted zero-activity days, so the chart renders one bar per day of the month.
export function fillDailyGaps(days, period) {
  if (!period) return days;
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return days;
  const byDate = new Map(days.map((d) => [d.date, d]));
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const filled = [];
  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = `${period}-${String(day).padStart(2, '0')}`;
    filled.push(byDate.get(date) || { date, credits: 0, messages: 0, costUsd: 0 });
  }
  return filled;
}
